/**
 * MeetingsPage.jsx
 * Video meetings (in-house WebRTC) · Voice calls (WebRTC) · Scheduling · Recording
 * Spacemancraft Pvt Ltd
 */
import { useState, useEffect, useRef } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, doc, addDoc, getDoc, setDoc,
  updateDoc, onSnapshot, query, orderBy,
  serverTimestamp, where, getDocs,
} from 'firebase/firestore';
import { getStorage, ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import VideoRoom, { VideoRoomLobby } from './VideoRoom';
import { withAlpha } from '../design/themes';

let _db = null, _stg = null;
const db  = () => { if (!_db)  { try { _db  = getFirestore(getApp()); } catch(_) {} } return _db; };
const stg = () => { if (!_stg) { try { _stg = getStorage(getApp());   } catch(_) {} } return _stg; };

const MONO = "'Share Tech Mono', monospace";
const MT = {
  panel: 'var(--smc-panel)',
  border: 'var(--smc-bd)',
  text: 'var(--smc-tx)',
  muted: 'var(--smc-tx3)',
  input: 'var(--smc-el)',
  deep: 'var(--smc-deep)',
  ac: 'var(--smc-ac)',
  acBg: 'var(--smc-ac-bg)',
  acBd: 'var(--smc-ac-border)',
  gn: 'var(--smc-gn)',
  gnBg: 'var(--smc-gn-bg)',
  rd: 'var(--smc-rd)',
  pu: 'var(--smc-pu)',
  puBg: 'var(--smc-pu-bg)',
  am: 'var(--smc-am)',
  shadow: 'var(--smc-shadow)',
};

const STUN = { iceServers: [{ urls:'stun:stun.l.google.com:19302' },{ urls:'stun:stun1.l.google.com:19302' }] };

function fmtDT(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-IN',{ day:'numeric', month:'short', year:'numeric' }) +
    ' ' + d.toLocaleTimeString('en-IN',{ hour:'2-digit', minute:'2-digit' });
}
function fmtDuration(sec) {
  const m = Math.floor(sec/60), s = sec%60;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}
function genRoomId() {
  return 'smc-' + Math.random().toString(36).slice(2,10) + Date.now().toString(36);
}

function VoiceCallRoom({ callId, isInitiator, localUser, remoteUser, onEnd }) {
  const pcRef         = useRef(null);
  const localStream   = useRef(null);
  const recorderRef   = useRef(null);
  const recBlobs      = useRef([]);
  const [muted,       setMuted]    = useState(false);
  const [elapsed,     setElapsed]  = useState(0);
  const [recording,   setRecording]= useState(false);
  const [status,      setStatus]   = useState('connecting…');
  const [recSaved,    setRecSaved] = useState(false);
  const remoteAudioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setInterval(() => setElapsed(e => e+1), 1000);

    async function setup() {
      try {
        localStream.current = await navigator.mediaDevices.getUserMedia({ audio:true, video:false });
        const pc = new RTCPeerConnection(STUN);
        pcRef.current = pc;

        localStream.current.getTracks().forEach(t => pc.addTrack(t, localStream.current));

        pc.ontrack = e => {
          if (remoteAudioRef.current && e.streams[0]) {
            remoteAudioRef.current.srcObject = e.streams[0];
          }
          setStatus('connected');
        };

        const callRef = doc(db(),'calls',callId);
        const myCol = isInitiator ? 'callerCandidates' : 'calleeCandidates';
        pc.onicecandidate = async e => {
          if (e.candidate) {
            await addDoc(collection(db(),'calls',callId,myCol), e.candidate.toJSON());
          }
        };

        if (isInitiator) {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          await updateDoc(callRef, { offer: { type: offer.type, sdp: offer.sdp } });

          const unsub = onSnapshot(callRef, async snap => {
            const data = snap.data();
            if (!pc.currentRemoteDescription && data?.answer && !cancelled) {
              await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
            }
          });
          const unsubIce = onSnapshot(collection(db(),'calls',callId,'calleeCandidates'), snap => {
            snap.docChanges().forEach(change => {
              if (change.type === 'added') pc.addIceCandidate(new RTCIceCandidate(change.doc.data()));
            });
          });
          return () => { unsub(); unsubIce(); };
        } else {
          const snap = await getDoc(callRef);
          const data = snap.data();
          if (data?.offer) {
            await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            await updateDoc(callRef, { answer: { type: answer.type, sdp: answer.sdp }, status: 'active' });
          }
          const unsubIce = onSnapshot(collection(db(),'calls',callId,'callerCandidates'), snap => {
            snap.docChanges().forEach(change => {
              if (change.type === 'added') pc.addIceCandidate(new RTCIceCandidate(change.doc.data()));
            });
          });
          return () => { unsubIce(); };
        }
      } catch(e) {
        setStatus('failed: ' + e.message);
      }
    }

    const unsubCall = onSnapshot(doc(db(),'calls',callId), snap => {
      if (snap.data()?.status === 'ended') endCall(false);
    });

    const cleanup = setup();
    return () => {
      cancelled = true;
      clearInterval(timer);
      cleanup.then?.(fn => fn?.());
      unsubCall();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  function toggleMute() {
    if (localStream.current) {
      localStream.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setMuted(m => !m);
    }
  }

  function startRecording() {
    if (!localStream.current) return;
    recBlobs.current = [];
    recorderRef.current = new MediaRecorder(localStream.current);
    recorderRef.current.ondataavailable = e => { if (e.data.size > 0) recBlobs.current.push(e.data); };
    recorderRef.current.onstop = saveRecording;
    recorderRef.current.start(1000);
    setRecording(true);
  }

  async function saveRecording() {
    if (!recBlobs.current.length) return;
    const blob = new Blob(recBlobs.current, { type:'audio/webm' });
    const path = `recordings/calls/${callId}/${Date.now()}.webm`;
    const ref  = sRef(stg(), path);
    await uploadBytes(ref, blob);
    const url  = await getDownloadURL(ref);
    await updateDoc(doc(db(),'calls',callId), { recordingUrl: url });
    setRecSaved(true);
  }

  async function endCall(local = true) {
    recorderRef.current?.stop();
    localStream.current?.getTracks().forEach(t => t.stop());
    pcRef.current?.close();
    if (local) await updateDoc(doc(db(),'calls',callId), { status:'ended', endedAt: serverTimestamp() });
    onEnd();
  }

  return (
    <div className="smc-modal-overlay" style={{ position:'fixed', inset:0, zIndex:500,
      background: withAlpha('var(--smc-tx)', '99'), backdropFilter:'blur(8px)',
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <audio ref={remoteAudioRef} autoPlay style={{ display:'none' }} />
      <div style={{ background:MT.panel, border:`0.5px solid ${MT.border}`, borderRadius:20, padding:40,
        width:320, textAlign:'center', boxShadow:MT.shadow }}>
        <div style={{ fontSize:56, marginBottom:16 }}>📞</div>
        <div style={{ fontFamily:MONO, fontSize:14, fontWeight:700, color:MT.text, marginBottom:4 }}>
          {isInitiator ? remoteUser : localUser}
        </div>
        <div style={{ fontFamily:MONO, fontSize:11, color:MT.muted, marginBottom:24 }}>
          {status} {status === 'connected' ? `· ${fmtDuration(elapsed)}` : ''}
        </div>

        <div style={{ display:'flex', justifyContent:'center', gap:16, marginBottom:24 }}>
          <button onClick={toggleMute}
            style={{ width:52, height:52, borderRadius:'50%', border:'none', cursor:'pointer', fontSize:20,
              background: muted ? MT.rd : MT.deep, color: muted ? '#fff' : MT.muted }}>
            {muted ? '🔇' : '🎙'}
          </button>
          {!recording ? (
            <button onClick={startRecording}
              style={{ width:52, height:52, borderRadius:'50%', border:'none', cursor:'pointer', fontSize:20,
                background:MT.deep, color:MT.am }}>
              ⏺
            </button>
          ) : (
            <button onClick={() => { recorderRef.current?.stop(); setRecording(false); }}
              style={{ width:52, height:52, borderRadius:'50%', border:'none', cursor:'pointer', fontSize:20,
                background:MT.am, color:'#fff' }}>
              ⏹
            </button>
          )}
          <button onClick={() => endCall(true)}
            style={{ width:52, height:52, borderRadius:'50%', border:'none', cursor:'pointer', fontSize:20,
              background:MT.rd, color:'#fff' }}>
            📵
          </button>
        </div>
        {recSaved && <div style={{ fontFamily:MONO, fontSize:9, color:MT.gn }}>✓ Recording saved</div>}
      </div>
    </div>
  );
}

export function IncomingCallOverlay({ call, onAccept, onDecline }) {
  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    function ring() {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 440;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.8);
    }
    ring();
    const interval = setInterval(ring, 2000);
    return () => { clearInterval(interval); ctx.close(); };
  }, []);

  return (
    <div style={{ position:'fixed', top:24, right:24, zIndex:999,
      background:MT.panel, border:`0.5px solid ${MT.acBd}`, borderRadius:16,
      padding:'20px 24px', width:300, boxShadow:MT.shadow,
      animation:'slideIn 0.3s ease' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <div style={{ width:44, height:44, borderRadius:'50%', background:MT.acBg,
          border:`0.5px solid ${MT.acBd}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
          📞
        </div>
        <div>
          <div style={{ fontFamily:MONO, fontSize:13, fontWeight:700, color:MT.text }}>{call.callerName}</div>
          <div style={{ fontFamily:MONO, fontSize:10, color:MT.muted }}>Incoming voice call…</div>
        </div>
      </div>
      <div style={{ display:'flex', gap:10 }}>
        <button onClick={onAccept}
          style={{ flex:1, padding:'10px 0', background:MT.gn, border:'none',
            color:'#fff', fontFamily:MONO, fontSize:11, cursor:'pointer', borderRadius:8, fontWeight:700 }}>
          ANSWER
        </button>
        <button onClick={onDecline}
          style={{ flex:1, padding:'10px 0', background:MT.rd, border:'none',
            color:'#fff', fontFamily:MONO, fontSize:11, cursor:'pointer', borderRadius:8, fontWeight:700 }}>
          DECLINE
        </button>
      </div>
    </div>
  );
}

function TeamPicker({ selected, onChange }) {
  const [members, setMembers] = useState([]);
  const [allMode, setAllMode] = useState(selected === 'all');
  const [search,  setSearch]  = useState('');
  const q = search.trim().toLowerCase();
  const shown = members.filter(m => !q || `${m.name || ''} ${m.email || ''}`.toLowerCase().includes(q));

  useEffect(() => {
    getDocs(collection(db(), 'authorized_users')).then(snap =>
      setMembers(snap.docs.map(d => ({ id:d.id, ...d.data() }))
        .filter(m => m.active !== false && m.status !== 'revoked')
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '')))
    );
  }, []);

  return (
    <div>
      <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, cursor:'pointer' }}>
        <input type="checkbox" checked={allMode} onChange={e => {
          setAllMode(e.target.checked);
          onChange(e.target.checked ? 'all' : []);
        }} />
        <span style={{ fontFamily:MONO, fontSize:11, color:MT.text }}>Invite everyone</span>
      </label>
      {!allMode && (
        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members…"
            style={{ flex:'1 1 160px', background:MT.input, border:`0.5px solid ${MT.border}`, color:MT.text,
              padding:'7px 10px', fontFamily:MONO, fontSize:11, borderRadius:6 }} />
          <span style={{ fontFamily:MONO, fontSize:9, color:MT.pu }}>
            {Array.isArray(selected) ? selected.length : 0} SELECTED
          </span>
          <button type="button" onClick={() => onChange(shown.map(m => m.id))}
            style={{ padding:'4px 10px', fontFamily:MONO, fontSize:9, cursor:'pointer', borderRadius:6,
              background:'transparent', border:`0.5px solid ${MT.border}`, color:MT.muted }}>SELECT SHOWN</button>
          <button type="button" onClick={() => onChange([])}
            style={{ padding:'4px 10px', fontFamily:MONO, fontSize:9, cursor:'pointer', borderRadius:6,
              background:'transparent', border:`0.5px solid ${MT.border}`, color:MT.muted }}>CLEAR</button>
        </div>
      )}
      {!allMode && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:180, overflowY:'auto' }}>
          {shown.map(m => {
            const sel = Array.isArray(selected) && selected.includes(m.id);
            return (
              <button type="button" key={m.id} onClick={() => {
                const cur = Array.isArray(selected) ? selected : [];
                onChange(sel ? cur.filter(x=>x!==m.id) : [...cur, m.id]);
              }}
              style={{ padding:'5px 12px', fontFamily:MONO, fontSize:9, cursor:'pointer', borderRadius:20,
                background: sel ? MT.puBg : 'transparent',
                border:`0.5px solid ${sel ? MT.pu : MT.border}`,
                color: sel ? MT.pu : MT.muted }}>
                {m.name || m.email}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ScheduleForm({ user, onClose }) {
  const [title,        setTitle]       = useState('');
  const [type,         setType]        = useState('video');
  const [scheduledAt,  setScheduledAt] = useState('');
  const [duration,     setDuration]    = useState(60);
  const [invitees,     setInvitees]    = useState('all');
  const [recEnabled,   setRecEnabled]  = useState(false);
  const [desc,         setDesc]        = useState('');
  const [agenda,       setAgenda]      = useState([{ text: '', minutes: '' }]);
  const [saving,       setSaving]      = useState(false);
  const [err,          setErr]         = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!title.trim() || !scheduledAt) { setErr('Title and scheduled time are required'); return; }
    if (Array.isArray(invitees) && invitees.length === 0) { setErr('Select at least one member (or invite everyone)'); return; }
    const agendaItems = agenda.map(a => ({ text: a.text.trim(), minutes: Number(a.minutes) || null })).filter(a => a.text);
    setSaving(true); setErr('');
    try {
      const meetingId = doc(collection(db(),'meetings')).id;
      await setDoc(doc(db(),'meetings',meetingId), {
        id: meetingId,
        title: title.trim(), description: desc.trim(), type,
        scheduledAt: new Date(scheduledAt),
        duration, invitees,
        agenda: agendaItems,
        recordingEnabled: recEnabled,
        status: 'scheduled',
        createdBy: user.uid, createdByName: user.name,
        jitsiRoom: type === 'video' ? genRoomId() : null,
        recordings: [],
        createdAt: serverTimestamp(),
      });
      // Invitees are notified server-side (functions/notificationTriggers.js → MEETING_SCHEDULED)
      onClose();
    } catch(ex) { setErr(ex.message); setSaving(false); }
  }

  const inputStyle = {
    width:'100%', background:MT.input, border:`0.5px solid ${MT.border}`, color:MT.text,
    padding:'10px 14px', fontFamily:MONO, fontSize:12, borderRadius:6,
    boxSizing:'border-box', marginBottom:12,
  };

  return (
    <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:0 }}>
      <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Meeting title *" style={inputStyle} />
      <textarea value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Description (optional)" rows={2}
        style={{ ...inputStyle, resize:'vertical' }} />

      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        {[['video','🎥 Video Meeting'],['voice','📞 Voice Call']].map(([v,l]) => (
          <button key={v} type="button" onClick={() => setType(v)}
            style={{ flex:1, padding:'10px 0', fontFamily:MONO, fontSize:10, cursor:'pointer', borderRadius:6,
              background: type===v ? (v==='video' ? MT.puBg : MT.acBg) : 'transparent',
              border:`0.5px solid ${type===v ? (v==='video' ? MT.pu : MT.ac) : MT.border}`,
              color: type===v ? (v==='video' ? MT.pu : MT.ac) : MT.muted }}>
            {l}
          </button>
        ))}
      </div>

      <input type="datetime-local" value={scheduledAt} onChange={e=>setScheduledAt(e.target.value)} style={inputStyle} />

      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        <select value={duration} onChange={e=>setDuration(Number(e.target.value))}
          style={{ ...inputStyle, marginBottom:0, flex:1 }}>
          {[15,30,45,60,90,120].map(m => <option key={m} value={m}>{m} minutes</option>)}
        </select>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:MONO, fontSize:10, color:MT.muted, letterSpacing:1, marginBottom:8 }}>AGENDA</div>
        {agenda.map((a, i) => (
          <div key={i} style={{ display:'flex', gap:6, marginBottom:6, alignItems:'center' }}>
            <span style={{ fontFamily:MONO, fontSize:10, color:MT.muted, width:18 }}>{i + 1}.</span>
            <input value={a.text} placeholder={`Agenda item ${i + 1}`}
              onChange={e => setAgenda(p => p.map((x, j) => j === i ? { ...x, text: e.target.value } : x))}
              style={{ ...inputStyle, marginBottom:0, flex:1 }} />
            <input value={a.minutes} placeholder="min" inputMode="numeric" aria-label="Minutes"
              onChange={e => setAgenda(p => p.map((x, j) => j === i ? { ...x, minutes: e.target.value.replace(/\D/g, '') } : x))}
              style={{ ...inputStyle, marginBottom:0, width:64, flex:'0 0 64px' }} />
            <button type="button" aria-label="Remove agenda item" disabled={agenda.length === 1}
              onClick={() => setAgenda(p => p.filter((_, j) => j !== i))}
              style={{ background:'transparent', border:`0.5px solid ${MT.border}`, color:MT.muted, borderRadius:6,
                width:30, height:30, cursor:agenda.length === 1 ? 'default' : 'pointer', opacity:agenda.length === 1 ? 0.4 : 1 }}>✕</button>
          </div>
        ))}
        <button type="button" onClick={() => setAgenda(p => [...p, { text: '', minutes: '' }])}
          style={{ padding:'5px 12px', fontFamily:MONO, fontSize:9, cursor:'pointer', borderRadius:6,
            background:'transparent', border:`0.5px dashed ${MT.border}`, color:MT.ac }}>+ ADD AGENDA ITEM</button>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontFamily:MONO, fontSize:10, color:MT.muted, letterSpacing:1, marginBottom:8 }}>MEMBERS</div>
        <TeamPicker selected={invitees} onChange={setInvitees} />
      </div>

      <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20, cursor:'pointer' }}>
        <input type="checkbox" checked={recEnabled} onChange={e=>setRecEnabled(e.target.checked)} />
        <span style={{ fontFamily:MONO, fontSize:11, color:MT.text }}>
          {type==='video' ? '☁ Enable cloud recording (screen capture → Firebase Storage)' : '⏺ Enable call recording'}
        </span>
      </label>

      {err && <div style={{ fontFamily:MONO, fontSize:11, color:MT.rd, marginBottom:10 }}>{err}</div>}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <button type="submit" disabled={saving}
          style={{ flex:1, minWidth:140, padding:'12px 0', background:MT.acBg, border:`0.5px solid ${MT.acBd}`,
            color:MT.ac, fontFamily:MONO, fontSize:11, cursor:saving?'wait':'pointer',
            borderRadius:6, letterSpacing:1, fontWeight:700 }}>
          {saving ? 'SCHEDULING…' : 'SCHEDULE MEETING'}
        </button>
        <button type="button" onClick={onClose}
          style={{ padding:'12px 20px', background:'transparent', border:`0.5px solid ${MT.border}`,
            color:MT.muted, fontFamily:MONO, fontSize:11, cursor:'pointer', borderRadius:6 }}>
          CANCEL
        </button>
      </div>
    </form>
  );
}

function MeetingCard({ meeting, user, onJoin, onCall }) {
  const isUpcoming = meeting.scheduledAt?.toDate?.() > new Date();
  const typeColor  = meeting.type === 'video' ? MT.pu : MT.ac;
  const typeBg     = meeting.type === 'video' ? MT.puBg : MT.acBg;

  async function cancelMeeting() {
    if (!window.confirm('Cancel this meeting?')) return;
    await updateDoc(doc(db(),'meetings',meeting.id), { status:'cancelled' });
  }

  return (
    <div style={{ background:MT.panel, border:`0.5px solid ${MT.border}`, borderRadius:12,
      padding:'16px 20px', borderLeft:`4px solid ${typeColor}` }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:10, flexWrap:'wrap' }}>
        <span style={{ fontSize:28, flexShrink:0 }}>{meeting.type==='video' ? '🎥' : '📞'}</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, color:MT.text, fontSize:15, marginBottom:3 }}>{meeting.title}</div>
          <div style={{ fontFamily:MONO, fontSize:10, color:MT.muted }}>
            {fmtDT(meeting.scheduledAt)} · {meeting.duration} min · by {meeting.createdByName}
          </div>
          {meeting.description && <div style={{ fontSize:12, color:MT.muted, marginTop:4, lineHeight:1.5 }}>{meeting.description}</div>}
          <div style={{ fontFamily:MONO, fontSize:9, color:MT.muted, marginTop:4 }}>
            👥 {meeting.invitees === 'all' ? 'Everyone' : `${(meeting.invitees || []).length} member${(meeting.invitees || []).length === 1 ? '' : 's'}`}
          </div>
          {meeting.agenda?.length > 0 && (
            <ol style={{ margin:'8px 0 0', paddingLeft:18, fontSize:12, color:MT.text, lineHeight:1.6 }}>
              {meeting.agenda.map((a, i) => (
                <li key={i}>{a.text}{a.minutes ? <span style={{ fontFamily:MONO, fontSize:9, color:MT.muted }}> · {a.minutes} min</span> : null}</li>
              ))}
            </ol>
          )}
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
          <span style={{ fontFamily:MONO, fontSize:9, color:typeColor, background:typeBg,
            border:`0.5px solid ${withAlpha(typeColor, '33')}`, padding:'2px 8px', borderRadius:10 }}>
            {meeting.type === 'video' ? '🎥 VIDEO' : '📞 VOICE'}
          </span>
          {meeting.recordingEnabled && (
            <span style={{ fontFamily:MONO, fontSize:9, color:MT.am, background:'var(--smc-am-bg)',
              border:`0.5px solid ${withAlpha(MT.am, '33')}`, padding:'2px 8px', borderRadius:10 }}>⏺ REC</span>
          )}
        </div>
      </div>

      {meeting.recordings?.length > 0 && (
        <div style={{ marginBottom:10 }}>
          {meeting.recordings.map((r,i) => (
            <a key={i} href={r.url} target="_blank" rel="noreferrer"
              style={{ display:'inline-block', fontFamily:MONO, fontSize:9, color:MT.gn,
                background:MT.gnBg, border:`0.5px solid ${withAlpha(MT.gn, '33')}`, padding:'2px 10px',
                borderRadius:4, textDecoration:'none', marginRight:6 }}>
              ▶ Recording {i+1}
            </a>
          ))}
        </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {isUpcoming && meeting.status !== 'cancelled' && (
          meeting.type === 'video' ? (
            <button onClick={() => onJoin(meeting)}
              style={{ padding:'7px 16px', background:MT.puBg, border:`0.5px solid ${MT.pu}`,
                color:MT.pu, fontFamily:MONO, fontSize:10, cursor:'pointer', borderRadius:6, letterSpacing:1 }}>
              JOIN MEETING
            </button>
          ) : (
            <button onClick={() => onCall(meeting)}
              style={{ padding:'7px 16px', background:MT.acBg, border:`0.5px solid ${MT.acBd}`,
                color:MT.ac, fontFamily:MONO, fontSize:10, cursor:'pointer', borderRadius:6, letterSpacing:1 }}>
              START CALL
            </button>
          )
        )}
        {meeting.createdBy === user.uid && isUpcoming && meeting.status !== 'cancelled' && (
          <button onClick={cancelMeeting}
            style={{ padding:'7px 14px', background:'transparent', border:`0.5px solid ${MT.border}`,
              color:MT.muted, fontFamily:MONO, fontSize:10, cursor:'pointer', borderRadius:6 }}>
            CANCEL
          </button>
        )}
        {meeting.status === 'cancelled' && (
          <span style={{ fontFamily:MONO, fontSize:10, color:MT.rd }}>CANCELLED</span>
        )}
      </div>
    </div>
  );
}

function DirectCallPanel({ user, onActiveCall }) {
  const [members, setMembers] = useState([]);
  const [calling, setCalling] = useState(null);

  useEffect(() => {
    getDocs(collection(db(), 'authorized_users')).then(snap =>
      setMembers(snap.docs.map(d=>({id:d.id,...d.data()})).filter(m => m.id !== user.uid && m.active !== false))
    );
  }, [user.uid]);

  async function callMember(member) {
    setCalling(member.id);
    const callId = doc(collection(db(),'calls')).id;
    await setDoc(doc(db(),'calls',callId), {
      callerId: user.uid, callerName: user.name,
      calleeId: member.id, calleeName: member.name || member.email,
      status: 'ringing', type: 'voice',
      startedAt: serverTimestamp(),
    });
    onActiveCall({ id:callId, callerId:user.uid, callerName:user.name,
      calleeId:member.id, calleeName:member.name||member.email, status:'ringing' }, true);
    setCalling(null);
  }

  return (
    <div>
      <div style={{ fontFamily:MONO, fontSize:10, letterSpacing:2, color:MT.ac,
        borderBottom:`2px solid ${withAlpha(MT.ac, '22')}`, paddingBottom:8, marginBottom:16 }}>
        TEAM MEMBERS — DIRECT CALL
      </div>
      {members.length === 0 && <div style={{ color:MT.muted, fontFamily:MONO, fontSize:11 }}>No team members found.</div>}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {members.map(m => (
          <div key={m.id} style={{ background:MT.panel, border:`0.5px solid ${MT.border}`, borderRadius:10,
            padding:'12px 16px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:MT.deep,
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:MT.ac }}>
              {(m.name||m.email||'?')[0].toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:120 }}>
              <div style={{ fontWeight:600, color:MT.text, fontSize:13 }}>{m.name || m.email}</div>
              <div style={{ fontFamily:MONO, fontSize:9, color:MT.muted }}>{m.role || 'member'}</div>
            </div>
            <button onClick={() => callMember(m)} disabled={calling === m.id}
              style={{ padding:'6px 14px', background:MT.acBg, border:`0.5px solid ${MT.acBd}`,
                color:MT.ac, fontFamily:MONO, fontSize:9, cursor:'pointer', borderRadius:6 }}>
              {calling === m.id ? '📞 Calling…' : '📞 CALL'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MeetingsPage({ user }) {
  const [meetings,     setMeetings]     = useState([]);
  const [tab,          setTab]          = useState('upcoming');
  const [showSchedule, setShowSchedule] = useState(false);
  const [activeVideo,  setActiveVideo]  = useState(null);
  const [activeCall,   setActiveCall]   = useState(null);

  useEffect(() => {
    const q = query(collection(db(),'meetings'), orderBy('scheduledAt','desc'));
    return onSnapshot(q, snap => setMeetings(snap.docs.map(d=>({id:d.id,...d.data()}))), ()=>{});
  }, []);

  function handleJoin(meeting) {
    setActiveVideo({
      roomId: meeting.jitsiRoom || meeting.id,
      recordingEnabled: !!meeting.recordingEnabled,
    });
  }

  function handleActiveCall(callData, isInitiator) {
    setActiveCall({ ...callData, isInitiator });
  }

  const now     = new Date();
  const upcoming = meetings.filter(m => m.status !== 'cancelled' && m.scheduledAt?.toDate?.() >= now);
  const past     = meetings.filter(m => m.status === 'cancelled' || (m.scheduledAt?.toDate?.() < now));

  return (
    <div className="smc-meetings-page" style={{ color:MT.text, fontFamily:"'Inter',system-ui,sans-serif" }}>
      <style>{`
        @keyframes slideIn { from { transform:translateX(100%); opacity:0 } to { transform:translateX(0); opacity:1 } }
      `}</style>

      {activeVideo && (
        <VideoRoom
          roomId={activeVideo.roomId}
          user={user}
          recordingEnabled={activeVideo.recordingEnabled}
          onLeave={() => setActiveVideo(null)}
        />
      )}

      {activeCall && (
        <VoiceCallRoom
          callId={activeCall.id}
          isInitiator={activeCall.isInitiator}
          localUser={user.name}
          remoteUser={activeCall.isInitiator ? activeCall.calleeName : activeCall.callerName}
          onEnd={() => setActiveCall(null)}
        />
      )}

      <div className="smc-meetings-header" style={{ background:MT.panel, borderBottom:`0.5px solid ${MT.border}`,
        padding:'16px var(--smc-page-pad)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <span style={{ fontFamily:MONO, fontSize:14, fontWeight:700, color:MT.ac, letterSpacing:2 }}>
          📹 MEETINGS & CALLS
        </span>
        <div style={{ flex:1 }} />
        <button onClick={() => setShowSchedule(true)}
          style={{ padding:'9px 20px', background:MT.acBg, border:`0.5px solid ${MT.acBd}`,
            color:MT.ac, fontFamily:MONO, fontSize:10, cursor:'pointer', borderRadius:6, letterSpacing:1 }}>
          + SCHEDULE
        </button>
      </div>

      <div className="smc-tab-bar smc-meetings-tabs" style={{ borderBottom:`0.5px solid ${MT.border}`, padding:'0 var(--smc-page-pad)' }}>
        {[['upcoming','UPCOMING'],['past','PAST'],['calls','DIRECT CALL']].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ padding:'12px 20px', fontFamily:MONO, fontSize:10, letterSpacing:1,
              cursor:'pointer', background:'transparent', border:'none',
              borderBottom:`2px solid ${tab===v ? MT.ac : 'transparent'}`,
              color: tab===v ? MT.ac : MT.muted }}>
            {l}
          </button>
        ))}
      </div>

      {showSchedule && (
        <div className="smc-modal-overlay" style={{ position:'fixed', inset:0,
          background: withAlpha('var(--smc-tx)', '99'), zIndex:400,
          display:'flex', alignItems:'flex-start', justifyContent:'center',
          padding:'76px 16px 24px', overflowY:'auto' }}
          onClick={e => e.target===e.currentTarget && setShowSchedule(false)}>
          <div className="smc-modal-panel" style={{ background:MT.panel, border:`0.5px solid ${MT.border}`, borderRadius:14,
            width:'100%', maxWidth:540, padding:28 }}>
            <div style={{ fontFamily:MONO, fontSize:12, fontWeight:700, color:MT.ac,
              letterSpacing:2, marginBottom:20 }}>📅 SCHEDULE MEETING</div>
            <ScheduleForm user={user} onClose={() => setShowSchedule(false)} />
          </div>
        </div>
      )}

      <div className="smc-meetings-content" style={{ maxWidth:860, margin:'0 auto', padding:'24px var(--smc-page-pad)' }}>
        {tab === 'upcoming' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {upcoming.length === 0 && (
              <div style={{ textAlign:'center', padding:'60px 0', color:MT.muted, fontFamily:MONO }}>
                No upcoming meetings. Schedule one!
              </div>
            )}
            {upcoming.map(m => (
              <MeetingCard key={m.id} meeting={m} user={user}
                onJoin={handleJoin} onCall={() => {}} />
            ))}
          </div>
        )}
        {tab === 'past' && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {past.length === 0 && <div style={{ textAlign:'center', padding:'60px 0', color:MT.muted, fontFamily:MONO }}>No past meetings.</div>}
            {past.map(m => (
              <MeetingCard key={m.id} meeting={m} user={user} onJoin={()=>{}} onCall={()=>{}} />
            ))}
          </div>
        )}
        {tab === 'calls' && (
          <div>
            <VideoRoomLobby
              user={user}
              onJoin={roomId => setActiveVideo({ roomId, recordingEnabled: true })}
            />
            <div style={{ borderTop:`0.5px solid ${MT.border}`, margin:'24px 0' }} />
            <DirectCallPanel user={user} onActiveCall={handleActiveCall} />
          </div>
        )}
      </div>
    </div>
  );
}

export function useCallListener(userId, onIncomingCall) {
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db(), 'calls'),
      where('calleeId', '==', userId),
      where('status',   '==', 'ringing')
    );
    return onSnapshot(q, snap => {
      snap.docChanges().forEach(change => {
        if (change.type === 'added') onIncomingCall({ id: change.doc.id, ...change.doc.data() });
      });
    }, () => {});
  }, [userId, onIncomingCall]);
}
